import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { questionsAPI } from '../../lib/api';
import { GitBranch, Users, Sparkles, ChevronRight } from 'lucide-react';

export const RelatedMindmaps = ({ questionId, compact = false }) => {
  const [related, setRelated] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchRelated = async () => {
      try {
        const response = await questionsAPI.getRelated(questionId, 5);
        setRelated(response.data.related || []);
        setKeywords(response.data.keywords || []);
      } catch (error) {
        console.error('Error fetching related mindmaps:', error);
      } finally {
        setLoading(false);
      }
    };

    if (questionId) {
      fetchRelated();
    }
  }, [questionId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="animate-pulse">
          <div className="h-5 bg-slate-200 rounded w-32 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-slate-100 rounded"></div>
            <div className="h-16 bg-slate-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (related.length === 0) {
    return null; // Don't show section if no related mindmaps
  }

  const displayedMindmaps = expanded ? related : related.slice(0, compact ? 2 : 3);

  // Compact version for mobile tab
  if (compact) {
    return (
      <div className="space-y-2" data-testid="related-mindmaps-compact">
        {displayedMindmaps.map((mindmap) => (
          <Link
            key={mindmap.question_id}
            to={`/q/${mindmap.question_id}`}
            className="flex items-center justify-between gap-2 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors group"
            data-testid={`related-mindmap-compact-${mindmap.question_id}`}
          >
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-slate-800 text-xs line-clamp-1 group-hover:text-orange-600 transition-colors">
                {mindmap.title}
              </h4>
              <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                <span className="flex items-center gap-0.5">
                  <GitBranch className="w-2.5 h-2.5" />
                  {mindmap.node_count}
                </span>
                <span className="flex items-center gap-0.5">
                  <Users className="w-2.5 h-2.5" />
                  {mindmap.contributor_count}
                </span>
              </div>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-orange-500 transition-colors flex-shrink-0" />
          </Link>
        ))}
        {related.length > 2 && (
          <p className="text-[10px] text-slate-400 text-center">
            +{related.length - 2} more on desktop sidebar
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="related-mindmaps">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-orange-500" />
          <h3 className="font-semibold text-slate-900 text-sm">Related Mindmaps</h3>
        </div>
        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {keywords.slice(0, 5).map((kw) => (
              <span
                key={kw}
                className="px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded-full"
              >
                {kw}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Mindmap List */}
      <div className="divide-y divide-slate-100">
        {displayedMindmaps.map((mindmap) => (
          <Link
            key={mindmap.question_id}
            to={`/q/${mindmap.question_id}`}
            className="block px-4 py-3 hover:bg-slate-50 transition-colors group"
            data-testid={`related-mindmap-${mindmap.question_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-slate-800 text-sm line-clamp-2 group-hover:text-orange-600 transition-colors">
                  {mindmap.title}
                </h4>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                  <span className="flex items-center gap-1">
                    <GitBranch className="w-3 h-3" />
                    {mindmap.node_count} nodes
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {mindmap.contributor_count}
                  </span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-orange-500 transition-colors flex-shrink-0 mt-1" />
            </div>
          </Link>
        ))}
      </div>

      {/* Show More/Less */}
      {related.length > 3 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-2 text-xs text-orange-600 hover:bg-orange-50 transition-colors border-t border-slate-100"
          data-testid="toggle-related"
        >
          {expanded ? 'Show Less' : `Show ${related.length - 3} More`}
        </button>
      )}
    </div>
  );
};

export default RelatedMindmaps;
